import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  GraduationCap, 
  Search, 
  FileText, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Building2,
  Users,
  TrendingUp,
  Target,
  BookOpen,
  Award,
  Briefcase,
  Lightbulb
} from 'lucide-react';

export default function MarchesPublicsFormationPage() {
  return (
    <>
      <SEO 
        title="Marchés Publics Formation : Appels d'Offres Organismes de Formation"
        description="Comment trouver des marchés publics de formation professionnelle. Guide complet : appels d'offres formation, analyse DCE, certifications Qualiopi. Conseils pour organismes de formation."
        path="/marches-publics-formation"
      />
      <div className="min-h-screen bg-white">
        <Header />
        
        <Breadcrumb 
          items={[
            { label: 'Marchés publics formation' }
          ]} 
        />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-amber-50 via-yellow-50 to-white py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-6">
                <GraduationCap className="w-4 h-4" />
                Secteur Formation Professionnelle
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Marchés Publics Formation : Comment Trouver des Appels d'Offres pour les Organismes de Formation
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
                Le secteur de la formation professionnelle représente un marché important dans la commande publique. 
                Découvrez comment identifier les opportunités et remporter des marchés de formation 
                pour les collectivités, Pôle emploi, France Travail et les OPCO.
              </p>
              <Link to="/">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700">
                  <Search className="w-5 h-5 mr-2" />
                  Trouver des marchés formation
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { value: '32 Md€', label: 'dépenses formation/an', icon: TrendingUp },
                { value: '67 000', label: 'organismes de formation', icon: Building2 },
                { value: 'Qualiopi', label: 'certification obligatoire', icon: Award },
                { value: '+12%', label: 'croissance annuelle', icon: Target }
              ].map((stat, i) => (
                <div key={`item-${i}`} className="bg-white rounded-xl p-6 text-center shadow-sm">
                  <stat.icon className="w-8 h-8 text-amber-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            
            {/* Section 1 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <GraduationCap className="w-8 h-8 text-amber-600" />
                Quels types de marchés publics en formation ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Les <strong>marchés publics de formation</strong> couvrent un large spectre, 
                  de la formation des agents publics à l'insertion professionnelle. 
                  Voici les principales catégories :
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                {[
                  { title: 'Formation des agents publics', desc: 'Management, bureautique, langues, réglementaire, marchés publics', icon: Briefcase },
                  { title: 'Formation des demandeurs d\'emploi', desc: 'Préparation opérationnelle à l\'emploi (POE), parcours qualifiants', icon: Users },
                  { title: 'Insertion professionnelle', desc: 'Accompagnement, remise à niveau, compétences transversales', icon: Target },
                  { title: 'Formation métiers techniques', desc: 'BTP, industrie, numérique, santé, services à la personne', icon: Award }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="bg-amber-50 rounded-xl p-5 border border-amber-100">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-amber-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.title}</h3>
                        <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-6 rounded-r-lg">
                <p className="text-amber-800">
                  <strong>Depuis janvier 2022 :</strong> La certification <strong>Qualiopi</strong> est obligatoire 
                  pour tout organisme de formation souhaitant bénéficier de fonds publics ou mutualisés. 
                  C'est un prérequis pour répondre à la majorité des marchés publics de formation.
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Search className="w-8 h-8 text-amber-600" />
                Où trouver les appels d'offres formation ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-6">
                <p>
                  Les marchés de formation sont publiés par des acheteurs variés. 
                  Voici les principaux donneurs d'ordre à surveiller :
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { 
                    name: 'France Travail (ex-Pôle emploi)', 
                    desc: 'Premier acheteur de formation en France',
                    detail: 'POE, AFPR, formations conventionnées, marchés régionaux'
                  },
                  { 
                    name: 'Conseils régionaux', 
                    desc: 'Compétence formation professionnelle des jeunes et des adultes',
                    detail: 'Programmes régionaux de formation (PRF), qualifications'
                  },
                  { 
                    name: 'OPCO', 
                    desc: '11 opérateurs de compétences sectoriels',
                    detail: 'Actions collectives, formations métiers, alternance'
                  },
                  { 
                    name: 'Collectivités territoriales', 
                    desc: 'Mairies, départements, intercommunalités',
                    detail: 'Formation des agents territoriaux, CNFPT'
                  },
                  { 
                    name: 'Ministères et établissements publics', 
                    desc: 'Administration centrale et services déconcentrés',
                    detail: 'Formation continue des agents de l\'État'
                  }
                ].map((source, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:border-amber-300 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900">{source.name}</h3>
                      <p className="text-slate-600 text-sm">{source.desc}</p>
                      <span className="text-xs text-amber-600 font-medium">{source.detail}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-amber-600 text-white rounded-2xl p-6 mt-8">
                <h3 className="text-xl font-bold mb-3">Automatisez votre veille formation</h3>
                <p className="text-amber-100 mb-4">
                  Avec notre <Link to="/veille-marches-publics" className="underline font-medium text-white">veille marchés publics</Link>, 
                  recevez automatiquement les appels d'offres de formation correspondant à vos domaines d'expertise 
                  et à votre zone d'intervention.
                </p>
                <Link to="/">
                  <Button variant="secondary" className="bg-white text-amber-600 hover:bg-amber-50">
                    Configurer ma veille formation
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <FileText className="w-8 h-8 text-amber-600" />
                Comment analyser un DCE de formation ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-6">
                <p>
                  Les DCE de marchés de formation comportent des éléments spécifiques. 
                  Voici les points clés à identifier :
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { step: '1', title: 'Objectifs pédagogiques', desc: 'Compétences visées, certifications préparées, blocs de compétences RNCP' },
                  { step: '2', title: 'Public cible', desc: 'Profil des stagiaires, prérequis, nombre de personnes à former' },
                  { step: '3', title: 'Modalités pédagogiques', desc: 'Présentiel, distanciel, mixte, AFEST, durée des parcours' },
                  { step: '4', title: 'Indicateurs de performance', desc: 'Taux de réussite exigé, insertion professionnelle, satisfaction' },
                  { step: '5', title: 'Moyens exigés', desc: 'Qualifications des formateurs, plateaux techniques, locaux' }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1 bg-slate-50 rounded-xl p-4">
                      <h3 className="font-semibold text-slate-900">{item.title}</h3>
                      <p className="text-slate-600 text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-slate-600 mt-6">
                Notre <Link to="/analyse-dce" className="text-amber-600 font-medium hover:underline">outil d'analyse DCE par IA</Link> extrait 
                automatiquement les objectifs pédagogiques, le public cible et les critères d'évaluation.
              </p>
            </div>

            {/* Section 4 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
                Les erreurs fréquentes des organismes de formation
              </h2>
              
              <div className="space-y-4">
                {[
                  { 
                    error: 'Répondre sans certification Qualiopi',
                    solution: 'Vérifiez que votre certification est à jour et couvre les catégories d\'actions concernées par le marché.'
                  },
                  { 
                    error: 'Proposer un programme générique',
                    solution: 'Personnalisez votre ingénierie pédagogique en fonction du public et des objectifs spécifiques du marché.'
                  },
                  { 
                    error: 'Négliger les indicateurs de résultat',
                    solution: 'Les acheteurs exigent des engagements sur les taux de réussite et d\'insertion. Soyez réaliste mais ambitieux.'
                  },
                  { 
                    error: 'Sous-estimer les moyens humains',
                    solution: 'Présentez des formateurs qualifiés avec CV et références. La qualité de l\'équipe pédagogique est clé.'
                  },
                  { 
                    error: 'Ignorer les spécificités du public',
                    solution: 'Adaptez votre approche pédagogique au public (demandeurs d\'emploi, salariés, publics en insertion...).'
                  }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-red-600 font-bold text-sm">✗</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.error}</h3>
                        <div className="flex items-start gap-2 mt-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                          <p className="text-slate-600 text-sm">{item.solution}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Clock className="w-8 h-8 text-amber-600" />
                Comment gagner du temps dans la veille formation ?
              </h2>
              
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Les clés d'une veille efficace</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Mots-clés à surveiller</h4>
                    <ul className="space-y-2 text-slate-600 text-sm">
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Formation professionnelle</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Organisme de formation</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Actions de formation</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Prestations pédagogiques</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Insertion professionnelle</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Codes CPV formation</h4>
                    <ul className="space-y-2 text-slate-600 text-sm">
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 80500000 - Services de formation</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 80530000 - Formation professionnelle</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 80510000 - Formation spécialisée</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 80560000 - Formation santé/sécurité</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-amber-600 to-yellow-600 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Trouvez vos prochains marchés de formation
            </h2>
            <p className="text-amber-100 mb-8 max-w-xl mx-auto">
              Recevez gratuitement 3 recommandations de marchés publics 
              correspondant à vos domaines d'expertise.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-white text-amber-600 hover:bg-amber-50">
                <GraduationCap className="w-5 h-5 mr-2" />
                Commencer ma veille formation gratuite
              </Button>
            </Link>
          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPage="formation" />

        <Footer />
      </div>
    </>
  );
}
